import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box, List, ListItem, ListItemText, Typography, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Chip, OutlinedInput, Alert, Snackbar,
  IconButton, Menu, Avatar, AvatarGroup, Stack, Paper, Tooltip,
  Checkbox, ListItemAvatar, CircularProgress, Divider, Badge
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LabelIcon from '@mui/icons-material/Label';
import BusinessIcon from '@mui/icons-material/Business';
import HeaderPage from "../../../components/Header";
import { tokens } from "../../../theme";
import FullCalendar from '@fullcalendar/react';
import { useAuth } from "../../../AuthContext";

// ─── Activity config ──────────────────────────────────────────────────────────
const ACTIVITY_CONFIG = {
  Mia:        { color: '#6366f1', bg: '#6366f115', label: 'Mia' },
  Exonmobil:  { color: '#f59e0b', bg: '#f59e0b15', label: 'ExxonMobil' },
  Debmarine:  { color: '#06b6d4', bg: '#06b6d415', label: 'Debmarine' },
  BON:        { color: '#10b981', bg: '#10b98115', label: 'BON' },
};

const DISPLAY_TYPE_CONFIG = {
  public:  { color: '#10b981', bg: '#10b98120', label: 'Public' },
  private: { color: '#8b5cf6', bg: '#8b5cf620', label: 'Private' },
};

// ─── Styled chip helper ───────────────────────────────────────────────────────
const StyledChip = ({ label, color, bg, size = 'small', icon }) => (
  <Chip
    label={label}
    size={size}
    icon={icon}
    sx={{
      bgcolor: bg,
      color: color,
      border: `1px solid ${color}40`,
      fontWeight: 600,
      fontSize: '0.7rem',
      letterSpacing: '0.03em',
      '& .MuiChip-icon': { color: `${color} !important` },
    }}
  />
);

// ─── Sidebar Event Card ───────────────────────────────────────────────────────
const SidebarEventCard = ({ event, onClick, getAssignedStaffWithImages }) => {
  const activity = event.extendedProps?.activity;
  const actCfg = ACTIVITY_CONFIG[activity] || { color: '#94a3b8', bg: '#94a3b810' };
  const staff = getAssignedStaffWithImages(event.extendedProps?.assigned_to || []);

  return (
    <Box
      onClick={onClick}
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: '10px',
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.2s ease',
        borderLeft: `3px solid ${actCfg.color}`,
        '&:hover': {
          background: 'rgba(255,255,255,0.07)',
          transform: 'translateX(3px)',
          boxShadow: `0 4px 20px ${actCfg.color}20`,
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5, fontSize: '0.8rem', lineHeight: 1.3 }}
        noWrap
      >
        {event.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <AccessTimeIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}>
          {event.start?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          {!event.allDay && ` · ${event.start?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {activity && (
            <StyledChip label={actCfg.label} color={actCfg.color} bg={actCfg.bg} />
          )}
          <StyledChip
            label={event.extendedProps?.display_Type}
            color={DISPLAY_TYPE_CONFIG[event.extendedProps?.display_Type]?.color || '#94a3b8'}
            bg={DISPLAY_TYPE_CONFIG[event.extendedProps?.display_Type]?.bg || '#94a3b810'}
          />
        </Box>

        {staff.length > 0 && (
          <AvatarGroup
            max={3}
            spacing={12}
            sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: '0.6rem', border: '1.5px solid rgba(255,255,255,0.15)' } }}
          >
            {staff.map((s, i) => (
              <Tooltip key={i} title={s.name}>
                <Avatar src={s.image || undefined} alt={s.name}>
                  {!s.image && s.name.charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        )}
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CalendarPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  const [currentEvents, setCurrentEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuEvent, setContextMenuEvent] = useState(null);
  const [viewEventDialog, setViewEventDialog] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);
  const calendarRef = useRef(null);

  const isAdminOrManager = useCallback(() => {
    const role = userRole?.toLowerCase();
    return ['supperadmin', 'admin', 'manager', 'ceo', 'coo'].includes(role);
  }, [userRole]);

  const getCurrentUserId = useCallback(() => userID, [userID]);

  const defaultFormData = useMemo(() => ({
    title: '',
    description: '',
    activity: '',
    event_type: 'timed',
    display_Type: 'private',
    startDate: '',
    endDate: '',
    assigned_to: [],
    order_number: 0,
  }), []);

  const [formData, setFormData] = useState(defaultFormData);

  const parseAssignedTo = useCallback((assignedTo) => {
    if (!assignedTo) return [];
    if (typeof assignedTo === 'string') {
      try { return JSON.parse(assignedTo); } catch { return []; }
    }
    return Array.isArray(assignedTo) ? assignedTo : [];
  }, []);

  const filterEventsByUser = useCallback((eventsData) => {
    if (!Array.isArray(eventsData)) return [];
    return eventsData.filter(event => {
      const currentUserId = getCurrentUserId();
      const assignedTo = event.assigned_to || [];
      const isAssigned = assignedTo.some(s => String(s.staff_id) === String(currentUserId));
      const isCreator = String(event.created_by) === String(currentUserId);
      if (isAdminOrManager()) return true;
      return event.display_Type === 'public' || isAssigned || isCreator;
    });
  }, [getCurrentUserId, isAdminOrManager]);

  const canEditEvent = useCallback((event) => {
    if (!event) return false;
    const currentUserId = getCurrentUserId();
    const isCreator = String(event.created_by) === String(currentUserId);
    const isAssigned = event.assigned_to?.some(s => String(s.staff_id) === String(currentUserId));
    if (isAdminOrManager() && event.display_Type === 'public') return true;
    return isCreator || isAssigned;
  }, [getCurrentUserId, isAdminOrManager]);

  const getStaffDetails = useCallback((staffId) => {
    const staff = staffs.find(s => String(s.Id) === String(staffId) || String(s.id) === String(staffId));
    if (!staff) return null;
    return {
      id: staff.Id || staff.id,
      name: `${staff.FirstName || ''} ${staff.LastName || ''}`.trim(),
      email: staff.email,
      department: staff.department,
      image: staff.image ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}` : null,
      role: staff.role,
    };
  }, [staffs]);

  const getAssignedStaffWithImages = useCallback((assignedTo) => {
    if (!assignedTo || assignedTo.length === 0) return [];
    return assignedTo.map(staff => {
      const details = getStaffDetails(staff.staff_id);
      return details || { id: staff.staff_id, name: staff.staff_name || 'Unknown', image: null };
    }).filter(Boolean);
  }, [getStaffDetails]);

  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [eventsRes, staffRes] = await Promise.all([
        getData('proccess.php', 'calender_V2'),
        getData('proccess.php', 'staffs'),
      ]);

      if (eventsRes.success) {
        const parsed = (Array.isArray(eventsRes.data) ? eventsRes.data : []).map(e => ({
          ...e,
          assigned_to: parseAssignedTo(e.assigned_to),
        }));
        setEvents(filterEventsByUser(parsed));
      }

      if (staffRes.success) {
        setStaffs(Array.isArray(staffRes.data) ? staffRes.data : []);
      }
    } catch (err) {
      console.error(err);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  }, [getData, parseAssignedTo, filterEventsByUser, showSnackbar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const buildDefaultAssignedTo = useCallback(() => {
    const currentUserId = getCurrentUserId();
    const currentStaff = staffs.find(s => String(s.Id) === String(currentUserId));
    return [{
      staff_id: currentUserId,
      staff_name: currentStaff ? `${currentStaff.FirstName || ''} ${currentStaff.LastName || ''}`.trim() : 'Current User',
    }];
  }, [getCurrentUserId, staffs]);

  const handleDateClick = useCallback((selected) => {
    setFormData({
      ...defaultFormData,
      event_type: selected.allDay ? 'alltime' : 'timed',
      display_Type: isAdminOrManager() ? 'public' : 'private',
      startDate: selected.dateStr,
      endDate: selected.dateStr,
      assigned_to: buildDefaultAssignedTo(),
    });
    setSelectedEvent(null);
    setOpenDialog(true);
  }, [defaultFormData, isAdminOrManager, buildDefaultAssignedTo]);

  const handleEventClick = useCallback((clickInfo) => {
    const { event } = clickInfo;
    const ep = event.extendedProps;
    setViewingEvent({
      id: event.id,
      title: event.title,
      description: ep.description || '',
      activity: ep.activity || '',
      event_type: ep.event_type,
      display_Type: ep.display_Type,
      start: event.start,
      end: event.end,
      assigned_to: ep.assigned_to || [],
      order_number: ep.order_number,
      created_by: ep.created_by,
    });
    setViewEventDialog(true);
  }, []);

  const handleEditEvent = useCallback(() => {
    setViewEventDialog(false);
    if (viewingEvent && canEditEvent(viewingEvent)) {
      setSelectedEvent(viewingEvent);
      setFormData({
        title: viewingEvent.title,
        description: viewingEvent.description || '',
        activity: viewingEvent.activity || '',
        event_type: viewingEvent.event_type || 'timed',
        display_Type: viewingEvent.display_Type || 'private',
        startDate: viewingEvent.start ? new Date(viewingEvent.start).toISOString().slice(0, 16) : '',
        endDate: viewingEvent.end ? new Date(viewingEvent.end).toISOString().slice(0, 16) : '',
        assigned_to: viewingEvent.assigned_to || [],
        order_number: viewingEvent.order_number || 0,
      });
      setOpenDialog(true);
    }
  }, [viewingEvent, canEditEvent]);

  const handleEventRightClick = useCallback((info) => {
    info.jsEvent.preventDefault();
    if (info.event?.extendedProps && canEditEvent(info.event.extendedProps)) {
      setAnchorEl(info.jsEvent.currentTarget);
      setContextMenuEvent(info.event);
    }
  }, [canEditEvent]);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setContextMenuEvent(null);
  }, []);

  const handleDeleteEvent = useCallback(async (eventToDelete) => {
    const target = eventToDelete || contextMenuEvent;
    if (!target) return;
    try {
      const response = await deleteData('proccess.php', 'calender_V2', target.id || target.id);
      if (response.success) {
        await fetchData();
        showSnackbar('Event deleted successfully', 'success');
      } else {
        showSnackbar(response.message || 'Error deleting event', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnackbar('Error deleting event', 'error');
    }
    handleMenuClose();
    setViewEventDialog(false);
  }, [contextMenuEvent, deleteData, fetchData, showSnackbar, handleMenuClose]);

  const handleFormSubmit = useCallback(async () => {
    if (!formData.title || !formData.startDate) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    try {
      const assignedToIds = (Array.isArray(formData.assigned_to) ? formData.assigned_to : []).map(staff => {
        if (staff.staff_id) return { staff_id: staff.staff_id, staff_name: staff.staff_name };
        if (staff.Id) {
          const info = staffs.find(s => String(s.Id) === String(staff.Id));
          return { staff_id: staff.Id, staff_name: info ? `${info.FirstName || ''} ${info.LastName || ''}`.trim() : 'Unknown' };
        }
        return staff;
      });

      const eventData = {
        title: formData.title,
        description: formData.description || '',
        activity: formData.activity || '',
        event_type: formData.event_type,
        display_Type: formData.display_Type,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        assigned_to: JSON.stringify(assignedToIds),
        order_number: formData.order_number || 0,
        created_by: selectedEvent?.created_by || getCurrentUserId(),
      };

      let response;
      if (selectedEvent) {
        if (!canEditEvent(selectedEvent)) {
          showSnackbar('You do not have permission to edit this event', 'error');
          return;
        }
        response = await updateData('proccess.php', 'calender_V2', eventData, selectedEvent.id);
      } else {
        response = await addData('proccess.php', 'calender_V2', eventData);
      }

      if (response.success) {
        showSnackbar(selectedEvent ? 'Event updated successfully' : 'Event created successfully', 'success');
        await fetchData();
        setOpenDialog(false);
        setFormData(defaultFormData);
        setSelectedEvent(null);
      } else {
        showSnackbar(response.message || 'Error saving event', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnackbar('Error saving event', 'error');
    }
  }, [formData, selectedEvent, staffs, getCurrentUserId, canEditEvent, updateData, addData, showSnackbar, fetchData, defaultFormData]);

  const handleEventsSet = useCallback((evts) => {
    setCurrentEvents(evts);
  }, []);

  // ─── Calendar events with colors ─────────────────────────────────────────
  const calendarEvents = useMemo(() => {
    return events.map(event => {
      const currentUserId = getCurrentUserId();
      const isAssigned = event.assigned_to?.some(s => String(s.staff_id) === String(currentUserId));
      const isCreator = String(event.created_by) === String(currentUserId);
      const actCfg = ACTIVITY_CONFIG[event.activity];

      let backgroundColor, borderColor;
      if (actCfg) {
        backgroundColor = actCfg.color;
        borderColor = actCfg.color;
      } else if (event.display_Type === 'public') {
        backgroundColor = '#10b981';
        borderColor = '#059669';
      } else if (isAssigned || isCreator) {
        backgroundColor = '#6366f1';
        borderColor = '#4f46e5';
      } else {
        backgroundColor = '#64748b';
        borderColor = '#475569';
      }

      return {
        id: event.id,
        title: event.title,
        start: event.startDate,
        end: event.endDate,
        allDay: event.event_type === 'alltime',
        extendedProps: {
          description: event.description,
          activity: event.activity,
          event_type: event.event_type,
          display_Type: event.display_Type,
          assigned_to: event.assigned_to,
          order_number: event.order_number,
          created_by: event.created_by,
        },
        backgroundColor,
        borderColor,
        textColor: '#ffffff',
      };
    });
  }, [events, getCurrentUserId]);

  // ─── Calendar global styles ───────────────────────────────────────────────
  const calendarSx = {
    '& .fc': {
      fontFamily: '"Inter", "DM Sans", sans-serif',
      '--fc-border-color': 'rgba(255,255,255,0.06)',
      '--fc-today-bg-color': 'rgba(99,102,241,0.08)',
      '--fc-page-bg-color': 'transparent',
      '--fc-neutral-bg-color': 'rgba(255,255,255,0.03)',
      '--fc-event-border-color': 'transparent',
    },
    '& .fc-toolbar-title': {
      fontSize: '1.1rem !important',
      fontWeight: '700 !important',
      color: 'rgba(255,255,255,0.9)',
      letterSpacing: '-0.02em',
    },
    '& .fc-button': {
      background: 'rgba(255,255,255,0.05) !important',
      border: '1px solid rgba(255,255,255,0.1) !important',
      borderRadius: '8px !important',
      color: 'rgba(255,255,255,0.7) !important',
      fontSize: '0.75rem !important',
      fontWeight: '600 !important',
      padding: '5px 12px !important',
      textTransform: 'capitalize !important',
      transition: 'all 0.2s !important',
      boxShadow: 'none !important',
      '&:hover': {
        background: 'rgba(99,102,241,0.2) !important',
        borderColor: 'rgba(99,102,241,0.4) !important',
        color: '#fff !important',
      },
    },
    '& .fc-button-active, & .fc-button-primary:not(:disabled).fc-button-active': {
      background: 'rgba(99,102,241,0.3) !important',
      borderColor: 'rgba(99,102,241,0.6) !important',
      color: '#fff !important',
    },
    '& .fc-col-header-cell': {
      padding: '10px 0 !important',
      background: 'rgba(255,255,255,0.02) !important',
    },
    '& .fc-col-header-cell-cushion': {
      color: 'rgba(255,255,255,0.5) !important',
      fontSize: '0.7rem !important',
      fontWeight: '700 !important',
      textTransform: 'uppercase !important',
      letterSpacing: '0.08em !important',
      textDecoration: 'none !important',
    },
    '& .fc-daygrid-day-number': {
      color: 'rgba(255,255,255,0.6) !important',
      fontSize: '0.8rem !important',
      fontWeight: '500 !important',
      textDecoration: 'none !important',
      padding: '6px 10px !important',
    },
    '& .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': {
      background: '#6366f1 !important',
      color: '#fff !important',
      borderRadius: '50% !important',
      width: '26px !important',
      height: '26px !important',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
    },
    '& .fc-event': {
      borderRadius: '6px !important',
      padding: '2px 6px !important',
      fontSize: '0.72rem !important',
      fontWeight: '600 !important',
      border: 'none !important',
      cursor: 'pointer',
      transition: 'opacity 0.15s, transform 0.15s !important',
      '&:hover': {
        opacity: 0.85,
        transform: 'translateY(-1px)',
      },
    },
    '& .fc-list-event': {
      cursor: 'pointer',
      '&:hover td': { background: 'rgba(255,255,255,0.04) !important' },
    },
    '& .fc-list-event-title a': {
      color: 'rgba(255,255,255,0.85) !important',
      textDecoration: 'none !important',
      fontSize: '0.82rem !important',
    },
    '& .fc-list-day-cushion': {
      background: 'rgba(99,102,241,0.12) !important',
    },
    '& .fc-list-day-text, & .fc-list-day-side-text': {
      color: 'rgba(255,255,255,0.7) !important',
      fontSize: '0.78rem !important',
      fontWeight: '700 !important',
      textDecoration: 'none !important',
    },
    '& .fc-timegrid-slot-label': {
      color: 'rgba(255,255,255,0.35) !important',
      fontSize: '0.68rem !important',
    },
    '& .fc-scrollgrid': {
      borderColor: 'rgba(255,255,255,0.06) !important',
    },
    '& .fc-scrollgrid td, & .fc-scrollgrid th': {
      borderColor: 'rgba(255,255,255,0.06) !important',
    },
    '& .fc-more-link': {
      color: '#6366f1 !important',
      fontSize: '0.7rem !important',
      fontWeight: '700 !important',
    },
  };

  // ─── Field helper ─────────────────────────────────────────────────────────
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '10px',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
    '& .MuiInputBase-input': { color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1' }} size={40} thickness={3} />
          <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
            Loading calendar…
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <HeaderPage title="CALENDAR" subtitle="Schedule & Event Management" />

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            borderRadius: '10px',
            background: snackbar.severity === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${snackbar.severity === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: '#fff',
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: snackbar.severity === 'success' ? '#10b981' : '#ef4444' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Activity Legend ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', mr: 0.5, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Activities:
        </Typography>
        {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.color }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
              {cfg.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Main Layout ───────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" gap={2.5} height="82vh">

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            width: '260px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Add event button */}
          <Button
            startIcon={<AddIcon />}
            fullWidth
            onClick={() => {
              setFormData({
                ...defaultFormData,
                display_Type: isAdminOrManager() ? 'public' : 'private',
                assigned_to: buildDefaultAssignedTo(),
              });
              setSelectedEvent(null);
              setOpenDialog(true);
            }}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: '10px',
              py: 1.2,
              fontSize: '0.8rem',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(99,102,241,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                boxShadow: '0 6px 20px rgba(99,102,241,0.5)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            New Event
          </Button>

          {/* Upcoming events */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '12px',
              p: 1.5,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '4px' },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Upcoming Events
            </Typography>

            {currentEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EventIcon sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 36, mb: 1 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', fontSize: '0.75rem' }}>
                  No upcoming events
                </Typography>
              </Box>
            ) : (
              currentEvents.slice(0, 15).map(event => (
                <SidebarEventCard
                  key={event.id}
                  event={event}
                  getAssignedStaffWithImages={getAssignedStaffWithImages}
                  onClick={() => {
                    if (calendarRef.current) {
                      calendarRef.current.getApi().gotoDate(event.start);
                    }
                  }}
                />
              ))
            )}
          </Box>
        </Box>

        {/* ── Calendar ─────────────────────────────────────────────────────── */}
        <Box
          flex={1}
          sx={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            p: 1.5,
            overflow: 'hidden',
            ...calendarSx,
          }}
        >
          <FullCalendar
            ref={calendarRef}
            height="100%"
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            editable={isAdminOrManager()}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={3}
            weekends={true}
            events={calendarEvents}
            select={handleDateClick}
            eventClick={handleEventClick}
            eventDidMount={(info) => {
              info.el.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                handleEventRightClick({ jsEvent: e, event: info.event });
              });
            }}
            eventsSet={handleEventsSet}
          />
        </Box>
      </Box>

      {/* ── Create / Edit Dialog ──────────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15,17,26,0.97)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', fontSize: '1rem', letterSpacing: '-0.02em' }}>
                {selectedEvent ? 'Edit Event' : 'Create Event'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
                {selectedEvent ? 'Update the event details below' : 'Fill in the event details below'}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDialog(false)} size="small"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.06)' }} />
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            <TextField label="Title *" fullWidth value={formData.title}
              onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} sx={fieldSx} />

            <TextField label="Description" fullWidth multiline rows={2} value={formData.description}
              onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} sx={fieldSx} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Activity */}
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Activity</InputLabel>
                <Select value={formData.activity} onChange={e => setFormData(f => ({ ...f, activity: e.target.value }))} label="Activity">
                  <MenuItem value=""><em>None</em></MenuItem>
                  {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.color }} />
                        {cfg.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Event type */}
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Event Type</InputLabel>
                <Select value={formData.event_type} onChange={e => setFormData(f => ({ ...f, event_type: e.target.value }))} label="Event Type">
                  <MenuItem value="timed">Timed</MenuItem>
                  <MenuItem value="alltime">All Day</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {isAdminOrManager() && (
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Visibility</InputLabel>
                <Select value={formData.display_Type} onChange={e => setFormData(f => ({ ...f, display_Type: e.target.value }))} label="Visibility">
                  <MenuItem value="public">Public — visible to everyone</MenuItem>
                  <MenuItem value="private">Private — assigned staff only</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Start *"
                type={formData.event_type === 'alltime' ? 'date' : 'datetime-local'}
                fullWidth value={formData.startDate}
                onChange={e => setFormData(f => ({ ...f, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={fieldSx}
              />
              <TextField
                label="End"
                type={formData.event_type === 'alltime' ? 'date' : 'datetime-local'}
                fullWidth value={formData.endDate}
                onChange={e => setFormData(f => ({ ...f, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={fieldSx}
              />
            </Box>

            {/* Assign staff */}
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Assign Staff</InputLabel>
              <Select
                multiple
                value={formData.assigned_to}
                onChange={e => setFormData(f => ({ ...f, assigned_to: e.target.value }))}
                input={<OutlinedInput label="Assign Staff" />}
                renderValue={(selected) => (
                  <AvatarGroup max={6} spacing={12} sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.65rem', border: '1.5px solid rgba(255,255,255,0.15)' } }}>
                    {selected.map((staff) => {
                      const d = getStaffDetails(staff.staff_id || staff.Id);
                      return (
                        <Tooltip key={staff.staff_id || staff.Id} title={staff.staff_name || d?.name || 'Unknown'}>
                          <Avatar src={d?.image || undefined}>
                            {((staff.staff_name || d?.name || 'U').charAt(0)).toUpperCase()}
                          </Avatar>
                        </Tooltip>
                      );
                    })}
                  </AvatarGroup>
                )}
                MenuProps={{ PaperProps: { sx: { background: 'rgba(15,17,26,0.97)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', backdropFilter: 'blur(20px)' } } }}
              >
                {staffs.map((staff) => {
                  const staffName = `${staff.FirstName || ''} ${staff.LastName || ''}`.trim();
                  const staffImage = staff.image ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}` : null;
                  const isChecked = formData.assigned_to.some(s => (s.staff_id === staff.Id) || (s.Id === staff.Id));
                  return (
                    <MenuItem key={staff.Id} value={{ staff_id: staff.Id, staff_name: staffName, staff_image: staffImage }}
                      sx={{ gap: 1.5, py: 1 }}>
                      <Avatar src={staffImage} sx={{ width: 30, height: 30, fontSize: '0.75rem' }}>
                        {staffName.charAt(0) || 'S'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: '#fff' }}>{staffName}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>{staff.department || staff.role}</Typography>
                      </Box>
                      <Checkbox checked={isChecked} size="small" sx={{ color: 'rgba(255,255,255,0.2)', '&.Mui-checked': { color: '#6366f1' } }} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { background: 'rgba(255,255,255,0.05)' } }}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: '8px',
              px: 3, boxShadow: '0 4px 15px rgba(99,102,241,0.35)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', boxShadow: '0 6px 20px rgba(99,102,241,0.5)' },
            }}>
            {selectedEvent ? 'Update Event' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Event Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={viewEventDialog}
        onClose={() => setViewEventDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15,17,26,0.97)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        {viewingEvent && (() => {
          const actCfg = ACTIVITY_CONFIG[viewingEvent.activity];
          const dispCfg = DISPLAY_TYPE_CONFIG[viewingEvent.display_Type];
          const staff = getAssignedStaffWithImages(viewingEvent.assigned_to);

          return (
            <>
              {/* Coloured top bar */}
              <Box sx={{ height: 4, background: actCfg ? `linear-gradient(90deg, ${actCfg.color}, ${actCfg.color}80)` : 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: '16px 16px 0 0' }} />

              <DialogTitle sx={{ pb: 0, pt: 2, px: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', fontSize: '1rem', letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                      {viewingEvent.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                      {viewingEvent.activity && actCfg && (
                        <StyledChip label={actCfg.label} color={actCfg.color} bg={actCfg.bg} icon={<BusinessIcon sx={{ fontSize: '12px !important' }} />} />
                      )}
                      {dispCfg && (
                        <StyledChip label={dispCfg.label} color={dispCfg.color} bg={dispCfg.bg} />
                      )}
                      <StyledChip
                        label={viewingEvent.event_type === 'alltime' ? 'All Day' : 'Timed'}
                        color="rgba(255,255,255,0.6)"
                        bg="rgba(255,255,255,0.06)"
                        icon={<AccessTimeIcon sx={{ fontSize: '12px !important' }} />}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {canEditEvent(viewingEvent) && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton onClick={handleEditEvent} size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#6366f1', background: 'rgba(99,102,241,0.1)' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDeleteEvent(viewingEvent)} size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.1)' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <IconButton onClick={() => setViewEventDialog(false)} size="small"
                      sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' } }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.06)' }} />
              </DialogTitle>

              <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                {/* Description */}
                {viewingEvent.description && (
                  <Box sx={{ mb: 2.5, p: 1.5, background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, fontSize: '0.82rem' }}>
                      {viewingEvent.description}
                    </Typography>
                  </Box>
                )}

                {/* Date/time row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
                  {[
                    { label: 'Start', value: viewingEvent.start ? new Date(viewingEvent.start).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: viewingEvent.event_type === 'alltime' ? undefined : 'short' }) : 'N/A' },
                    { label: 'End', value: viewingEvent.end ? new Date(viewingEvent.end).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: viewingEvent.event_type === 'alltime' ? undefined : 'short' }) : 'N/A' },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ p: 1.5, background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.25 }}>
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', fontWeight: 600 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Assigned staff */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Assigned Staff
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem' }}>
                      {staff.length} {staff.length === 1 ? 'member' : 'members'}
                    </Typography>
                  </Box>

                  {staff.length > 0 ? (
                    <>
                      <AvatarGroup
                        max={8}
                        spacing={16}
                        sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 36, height: 36, fontSize: '0.8rem', border: '2px solid rgba(255,255,255,0.1)', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.15)', zIndex: '10 !important' } } }}
                      >
                        {staff.map((s, i) => (
                          <Tooltip
                            key={i}
                            title={
                              <Box>
                                <Typography variant="caption" fontWeight={700} display="block">{s.name}</Typography>
                                {s.email && <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>{s.email}</Typography>}
                                {s.department && <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>{s.department}</Typography>}
                              </Box>
                            }
                          >
                            <Avatar src={s.image || undefined} alt={s.name}>
                              {!s.image && s.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </AvatarGroup>

                      {/* Staff name list */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.25 }}>
                        {staff.map((s, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, background: 'rgba(255,255,255,0.04)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                              {s.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem' }}>
                      No staff assigned
                    </Typography>
                  )}
                </Box>

                {/* Created by */}
                {viewingEvent.created_by && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', display: 'block' }}>
                    Created by {getStaffDetails(viewingEvent.created_by)?.name || 'Unknown'}
                  </Typography>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Button onClick={() => setViewEventDialog(false)}
                  sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { background: 'rgba(255,255,255,0.05)' } }}>
                  Close
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Context Menu ──────────────────────────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: 'rgba(15,17,26,0.97)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={handleEditEvent}
          sx={{ gap: 1.5, fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', py: 1.25, '&:hover': { background: 'rgba(99,102,241,0.1)', color: '#fff' } }}
        >
          <EditIcon sx={{ fontSize: 16 }} /> Edit Event
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteEvent(contextMenuEvent?.extendedProps)}
          sx={{ gap: 1.5, fontSize: '0.82rem', color: '#ef4444', py: 1.25, '&:hover': { background: 'rgba(239,68,68,0.1)' } }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} /> Delete Event
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CalendarPage;